import { EuroField, SeatCard } from '@/components/admin/seat-card';
import { catalogFrom, catalogSourceSentence } from '@/lib/model-catalog';
import {
  SEAT_KEY_GROUP,
  SEAT_KEY_LABELS,
  SEAT_KEY_NOTES,
  SEAT_KEY_ORDER,
  WARNING_WORDING,
  journalValue,
  movedSettings,
  pricingPath,
  seatOf,
  seatPath,
  seatPinOf,
  settingLabel,
} from '@/lib/model-labels';
import type {
  ModelCatalogView,
  ModelNumericPath,
  ModelParamChange,
  ModelParams,
  ModelParamsView,
  ModelSeat,
  ModelSeatKey,
  ModelTextPath,
} from '@matter_extractor/shared';
import { seatFingerprint } from '@matter_extractor/shared';

/**
 * Which models the dispositif runs on (§11.1) and what a step may spend on them
 * (§11.2), behind the operator guard in READING as in writing.
 *
 * Three things justify a screen rather than the `curl` the backend already
 * serves, and all three are here:
 *
 *  - a seat says what it COSTS before it is committed. The rate belongs to the
 *    seat since #61, so the card carries the two tariffs beside the model and
 *    recomputes the estimate as they are typed - shown at the only moment it
 *    can still change a mind.
 *  - the BUDGET of §11.2 sits on the same page as the rates it is compared
 *    against. A ceiling in euros read far from the euros it bounds is a number
 *    nobody can judge.
 *  - the journal of the moves made BEFORE the versions stays readable under the
 *    form (issue #192): a model changed at zero sessions is a calibration, the
 *    same change at fifteen is something else.
 *
 * Since issue #192 (vitrine.md section 5) this panel is the FORM of a versioned
 * singleton, the draft of « Les modèles »: it writes nothing and journals
 * nothing. The head of the versioned layer above it owns the autosave of the
 * draft, « Enregistrer une nouvelle version », the history and the pointers, and
 * words the refusals of a save from their `code` (`modelRefusalSentence`) -
 * the server's `message` is a developer's English string and never reaches
 * this screen. Colour stays at level 2 - black, white and bleu-nuit.
 */

export interface ModelPanelProps {
  /**
   * What sits BESIDE the form: the first-boot seed, the published tariffs, the
   * warnings and the journal of the moves made before the versions.
   */
  view: ModelParamsView;
  /** The seats of the `développement` version: what a moved seat is measured against. */
  stored: ModelParams;
  /** A version opened by « Voir », or a retired entity: nothing is typable. */
  readOnly?: boolean;
  /**
   * The providers' model lists (#175), or undefined while they are read or
   * when they could not be: the tiles then fall back on the built-in list.
   */
  catalogView?: ModelCatalogView | undefined;
  /** What the operator is editing: the draft of the version to come. */
  candidate: ModelParams;
  /** One text setting - a model id or an endpoint typed by hand. */
  onChange: (path: ModelTextPath, value: string | null) => void;
  /** One euro figure - a seat's tariff, or the budget of §11.2. */
  onNumber: (path: ModelNumericPath, value: number) => void;
  /**
   * A whole seat at once - route, model, endpoint AND tariff.
   *
   * A tile sets all four TOGETHER because it names one serve at one published
   * rate: writing them one at a time would put the candidate through states
   * nobody chose - a router route beside a direct id, which is precisely the
   * pair `route-model-mismatch` exists to name, or a new model still carrying
   * the previous one's rate, which is a euro count nothing on the screen could
   * point at.
   */
  onSeat: (seat: ModelSeatKey, next: ModelSeat) => void;
}

export function ModelPanel({
  view,
  stored,
  readOnly = false,
  catalogView,
  candidate,
  onChange,
  onNumber,
  onSeat,
}: ModelPanelProps) {
  const moved = movedSettings(stored, candidate);
  // The pins of the BASE get a tile of their own when no list names them any
  // more, so a seat still on last month's model shows it checked.
  const catalog = catalogFrom(
    catalogView,
    SEAT_KEY_ORDER.map((seat) => seatPinOf(stored, seat)),
  );

  return (
    // `inert` for a version read by « Voir »: nothing is focusable, clickable or
    // typable inside, and the layer's own banner says why.
    <div className="space-y-8" data-testid="modeles-panneau" {...(readOnly ? { inert: true } : {})}>
      {view.warnings.length > 0 ? <Warnings warnings={view.warnings} /> : null}

      {/*
        The four cards share their ROWS (BUILD-925).

        This screen exists to compare seats, and without a subgrid no line of one
        card meets its twin in the next: the seat notes are three lines on one
        and one on another, so « Fournisseur », « Modèle », the tariffs and the
        estimate all sat tens of pixels apart down the whole row - the eye could
        not scan across, on the one gesture the screen is for.

        `grid-rows-subgrid` rather than a minimum height on the note: a floor in
        pixels holds until somebody rewrites a sentence or resizes the window,
        and the next edit breaks the alignment silently. Here the row heights ARE
        the parent's, so the tallest note sets the row and all four cards move
        together whatever anybody writes.

        SEVEN rows because a seat card has seven blocks (see `SeatCard`): the
        span and the number of grid children must match, or the tail rows
        collapse into one.

        One column until `xl`, four after - the analyst of #149 beside the three
        others, as the planche `ReglagesModeles` draws them. Under 768 px - and
        all the way to 1280 - a card is the full width of the page, which is what
        keeps the two tariff fields, the tile rows and the fingerprint line from
        ever needing a horizontal scroll.
      */}
      <div data-testid="modeles-sieges" className="grid gap-6 xl:grid-cols-5">
        {SEAT_KEY_ORDER.map((seat) => (
          <SeatCard
            key={seat}
            id={seat}
            group={SEAT_KEY_GROUP[seat]}
            title={SEAT_KEY_LABELS[seat]}
            note={SEAT_KEY_NOTES[seat]}
            value={candidate[seat]}
            committed={seatPinOf(view.initial, seat)}
            stored={seatPinOf(stored, seat)}
            catalog={catalog}
            tariffs={view.tariffs}
            tilesSeat={seat}
            estimateSeat={seat}
            modelLabel={settingLabel(seatPath(seat, 'model'))}
            providerLabel={settingLabel(seatPath(seat, 'provider'))}
            subgrid
            onModel={(value) => onChange(seatPath(seat, 'model'), value)}
            onProvider={(value) => onChange(seatPath(seat, 'provider'), value)}
            onTariff={(field, value) => onNumber(pricingPath(seat, field), value)}
            onSeat={(next) => onSeat(seat, next)}
          />
        ))}
      </div>

      <p data-testid="modeles-catalogue-source" className="text-caption text-gray-600">
        {catalogSourceSentence(catalogView)} La saisie à la main reste ouverte sur chaque siège.
      </p>

      <BudgetField value={candidate.budgetPerStepEur} onNumber={onNumber} />

      {moved.some((path) => seatOf(path) !== null) ? (
        <FlotteWarning stored={stored} candidate={candidate} />
      ) : null}

      {view.journal.length > 0 ? <ChangeJournal journal={view.journal} /> : null}
    </div>
  );
}

/**
 * The AI budget of §11.2: ONE value in euros, applied to EACH STEP.
 *
 * Its own section under the seat cards rather than one more card, because it
 * belongs to no seat - it bounds what a step spends across all of them - and a
 * card would have said the opposite. The analyst's calls never count against it
 * (noyau.md §4.7): its determination is not a step. The help text says the two things H3
 * settles and that no operator can deduce from a number: it is per STEP and not
 * per session, and what happens when it is reached.
 */
function BudgetField({
  value,
  onNumber,
}: {
  value: number;
  onNumber: (path: ModelNumericPath, value: number) => void;
}) {
  return (
    <section
      aria-labelledby="budget-ia"
      data-testid="modeles-budget"
      className="space-y-3 rounded-md border-2 border-gray-200 p-5"
    >
      <h2 id="budget-ia" className="font-semibold text-bleu-nuit text-body-lg">
        Budget IA par étape
      </h2>
      <p className="max-w-3xl text-gray-700 text-small">
        Une seule valeur, en euros, appliquée à <strong>chaque étape</strong> d’un protocole -
        jamais à la session entière. Quand le coût cumulé des appels d’une étape la dépasse, l’étape
        est au plafond : elle ne relance plus, et la session passe à la suite. Le coût est compté au
        tarif de chaque siège ci-dessus, donc un siège sans tarif ne consomme rien et ce plafond ne
        le retient jamais.
      </p>
      <div className="max-w-56">
        <EuroField
          label="Plafond par étape"
          value={value}
          onNumber={(next) => onNumber('budgetPerStepEur', next)}
        />
      </div>
    </section>
  );
}

/**
 * What a FLOTTE move costs, said before the write.
 *
 * The sessions created afterwards run on something else than the ones before,
 * and a session already under way does not move - it finishes on what its
 * provenance names. That is the whole sentence, and it is worth a block of its
 * own because the field that moves a model gives no hint of it.
 *
 * The pin is shown whole - `<route>:<modèle>[@<hébergeur>]` - because the model
 * id alone does not say what ran: the same model served through the router is a
 * different serve, and the router does not cache the system prompt the direct
 * API caches.
 */
function FlotteWarning({ stored, candidate }: { stored: ModelParams; candidate: ModelParams }) {
  const moved = SEAT_KEY_ORDER.filter(
    (seat) =>
      seatFingerprint(seatPinOf(stored, seat)) !== seatFingerprint(seatPinOf(candidate, seat)),
  );
  // Only the tariffs moved: a real and ordinary case since #61, and one this
  // notice has nothing to say about - a rate changes what a step COUNTS, never
  // what a model answers.
  if (moved.length === 0) return null;

  return (
    <section
      aria-labelledby="avertissement-flotte"
      className="space-y-3 rounded-md border-2 border-bleu-nuit px-5 py-4"
    >
      <h2 id="avertissement-flotte" className="font-semibold text-bleu-nuit">
        Avant d’enregistrer
      </h2>
      <p className="text-gray-800 text-small">
        Les sessions créées après ce changement ne tournent plus sur les mêmes modèles que les
        précédentes. Rien n’est recalculé en arrière, et une session en cours ne bouge pas - elle
        finit sur ce que sa provenance nomme.
      </p>
      <dl className="space-y-3 text-small">
        {moved.map((seat) => (
          <div key={seat} className="space-y-1">
            <dt className="font-semibold text-gray-800">{SEAT_KEY_LABELS[seat]}</dt>
            <dd className="space-y-1 text-gray-700">
              <span className="block">
                Aujourd’hui :{' '}
                <code className="text-bleu-nuit">{seatFingerprint(seatPinOf(stored, seat))}</code>
              </span>
              <span className="block">
                Ensuite :{' '}
                <code className="text-bleu-nuit">
                  {seatFingerprint(seatPinOf(candidate, seat))}
                </code>
              </span>
            </dd>
          </div>
        ))}
      </dl>
      <p className="text-gray-700 text-small">
        Par le routeur, le prompt système n’est pas mis en cache : chaque tour le repaie plein
        tarif, là où l’API directe le relit à un dixième du prix.
      </p>
    </section>
  );
}

/** Informative, never blocking: refusing the unknown would block the future. */
function Warnings({ warnings }: { warnings: ModelParamsView['warnings'] }) {
  return (
    <div className="space-y-2 rounded-md border border-gray-300 px-5 py-4">
      <p className="font-semibold text-gray-900">À savoir :</p>
      <ul className="space-y-1 text-gray-800 text-small">
        {warnings.map((warning) => (
          <li key={`${warning.path}-${warning.code}`}>
            <strong>{settingLabel(warning.path)}</strong> - {WARNING_WORDING[warning.code]} (
            {warning.model}).
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The journal of the moves made BEFORE the versions (issue #192): nothing writes
 * it any more - a version and its history replace it - but what it holds stays
 * readable, and it is absent altogether on a deploy that never filled it.
 */
function ChangeJournal({ journal }: { journal: readonly ModelParamChange[] }) {
  return (
    <section aria-labelledby="journal-modeles" className="space-y-3">
      <h2 id="journal-modeles" className="font-semibold text-bleu-nuit text-body-lg">
        Ce qui a bougé avant les versions
      </h2>
      {journal.length === 0 ? null : (
        <ul className="space-y-2">
          {journal.map((change) => (
            <li
              key={`${change.changedAt}-${change.path}`}
              className="rounded-md border border-gray-200 bg-white p-3 text-small"
            >
              <span className="text-gray-600">{change.changedAt.slice(0, 10)}</span>{' '}
              <span className="font-semibold text-bleu-nuit">{change.changedBy}</span> a remplacé{' '}
              {settingLabel(change.path)} : {journalValue(change.path, change.from)} devient{' '}
              {journalValue(change.path, change.to)}, à {change.sessionCountAtChange} session
              {change.sessionCountAtChange > 1 ? 's' : ''} déjà menée
              {change.sessionCountAtChange > 1 ? 's' : ''}.
              {change.note === null ? null : (
                <span className="text-gray-700"> Motif : {change.note}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
